import { createContext, ReactNode, useEffect, useState } from 'react';

import type { UserResponse } from '@repo/contracts/tenant-api';

import { useGetCurrentUserQuery } from '@/features/users/api';
import { useAuth } from '@/lib/auth';

export type UserRole = {
  name: string;
  scope: {
    type: string;
    id: string;
  };
};

export type CurrentUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
};

type CurrentUserContextType = {
  currentUser: CurrentUser | null;
  setUser: (user: CurrentUser) => void;
  clearUser: () => void;
  hasRole: (roleName: string) => boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

export const CurrentUserContext = createContext<
  CurrentUserContextType | undefined
>(undefined);

function mapUserResponseToCurrentUser(response: UserResponse): CurrentUser {
  return {
    id: response.id,
    email: response.email,
    firstName: response.firstName ?? '',
    lastName: response.lastName ?? '',
    roles: (response.userRoles ?? []).map((ur) => ({
      name: ur.role.name,
      scope: {
        type: ur.role.scopeType ?? '',
        id: ur.role.scopeId ?? '',
      },
    })),
  };
}

export const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  // Fetch user profile when authenticated.
  // Global scope headers are already set by CurrentTenantProvider (parent).
  const {
    data: userResponse,
    isLoading: isUserFetching,
    isError,
    error: userError,
    refetch: refetchUser,
  } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated || isAuthLoading,
  });

  useEffect(() => {
    if (userResponse) {
      setCurrentUser(mapUserResponseToCurrentUser(userResponse));
    } else if (!isAuthenticated && !isAuthLoading) {
      setCurrentUser(null);
    }
  }, [userResponse, isAuthenticated, isAuthLoading]);

  const setUser = (user: CurrentUser) => {
    setCurrentUser(user);
  };

  const clearUser = () => {
    setCurrentUser(null);
  };

  const hasRole = (roleName: string) => {
    return !!currentUser?.roles.find((r) => r.name === roleName);
  };

  const isLoading = isAuthLoading || isUserFetching;

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        setUser,
        clearUser,
        hasRole,
        isLoading,
        isError,
        error: userError,
        refetch: refetchUser,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};
